import type { SelectOption } from '../types';

/**
 * Top ~50 cities for each US state, ordered roughly by population. Used by
 * the lead-form `City` select via {@link getCityOptions}. Lists are short
 * for states that don't have 50 incorporated places (HI, RI, WY, DE, etc.).
 *
 * Source: US Census Bureau population data, 2020 + post-2020 estimates.
 * Curated rather than exhaustive — a B2B lead form just needs the user's
 * rough geographic context, not every census-designated place. To switch
 * to the full ~19,500 incorporated places, replace this with a backend
 * loader + searchable combobox; the existing schema is unchanged.
 */

const city = (label: string): SelectOption => ({
    value: label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    label,
});

const cities = (...labels: string[]): SelectOption[] => labels.map(city);

export const US_CITY_OPTIONS_BY_STATE: Record<string, SelectOption[]> = {
    AL: cities(
        'Huntsville',
        'Birmingham',
        'Montgomery',
        'Mobile',
        'Tuscaloosa',
        'Hoover',
        'Auburn',
        'Dothan',
        'Decatur',
        'Madison',
        'Florence',
        'Vestavia Hills',
        'Gadsden',
        'Prattville',
        'Phenix City',
        'Alabaster',
        'Bessemer',
        'Opelika',
        'Northport',
        'Athens',
        'Pelham',
        'Anniston',
        'Mountain Brook',
        'Trussville',
        'Selma',
        'Helena',
        'Daphne',
        'Saraland',
        'Fairhope',
        'Talladega',
        'Homewood',
        'Cullman',
        'Center Point',
        'Hueytown',
        'Albertville',
        'Scottsboro',
        'Millbrook',
        'Calera',
        'Fort Payne',
        'Sylacauga',
        'Jasper',
        'Andalusia',
        'Troy',
        'Foley',
        'Eufaula',
        'Ozark',
        'Tuskegee',
        'Sheffield',
        'Tarrant',
        'Gardendale',
    ),
    AK: cities(
        'Anchorage',
        'Fairbanks',
        'Juneau',
        'Wasilla',
        'Sitka',
        'Ketchikan',
        'Kenai',
        'Kodiak',
        'Bethel',
        'Palmer',
        'Homer',
        'Soldotna',
        'Valdez',
        'Nome',
        'Utqiagvik',
        'Unalaska',
        'Cordova',
        'Petersburg',
        'Seward',
        'Wrangell',
        'Dillingham',
        'Kotzebue',
        'North Pole',
        'Hooper Bay',
        'Houston',
        'Sand Point',
        'Skagway',
        'Haines',
        'Talkeetna',
        'Delta Junction',
    ),
    AZ: cities(
        'Phoenix',
        'Tucson',
        'Mesa',
        'Chandler',
        'Scottsdale',
        'Glendale',
        'Gilbert',
        'Tempe',
        'Peoria',
        'Surprise',
        'Yuma',
        'Avondale',
        'Goodyear',
        'Flagstaff',
        'Buckeye',
        'Lake Havasu City',
        'Casa Grande',
        'Sierra Vista',
        'Maricopa',
        'Oro Valley',
        'Prescott',
        'Bullhead City',
        'Prescott Valley',
        'Apache Junction',
        'Marana',
        'El Mirage',
        'Kingman',
        'Queen Creek',
        'San Luis',
        'Sahuarita',
        'Fountain Hills',
        'Nogales',
        'Douglas',
        'Eloy',
        'Payson',
        'Somerton',
        'Paradise Valley',
        'Coolidge',
        'Cottonwood',
        'Camp Verde',
        'Chino Valley',
        'Show Low',
        'Sedona',
        'Winslow',
        'Globe',
        'Page',
        'Safford',
        'Bisbee',
        'Tolleson',
        'Wickenburg',
    ),
    AR: cities(
        'Little Rock',
        'Fort Smith',
        'Fayetteville',
        'Springdale',
        'Jonesboro',
        'Rogers',
        'Conway',
        'North Little Rock',
        'Bentonville',
        'Pine Bluff',
        'Hot Springs',
        'Benton',
        'Texarkana',
        'Sherwood',
        'Jacksonville',
        'Russellville',
        'Bella Vista',
        'West Memphis',
        'Paragould',
        'Cabot',
        'Searcy',
        'Van Buren',
        'El Dorado',
        'Maumelle',
        'Bryant',
        'Siloam Springs',
        'Blytheville',
        'Forrest City',
        'Harrison',
        'Mountain Home',
        'Marion',
        'Magnolia',
        'Camden',
        'Hope',
        'Stuttgart',
        'Wynne',
        'Greenwood',
        'Beebe',
        'Arkadelphia',
        'Pocahontas',
        'Newport',
        'Heber Springs',
        'Batesville',
        'Malvern',
        'Helena-West Helena',
        'Trumann',
        'Centerton',
        'Lowell',
        'Farmington',
        'Mena',
    ),
    CA: cities(
        'Los Angeles',
        'San Diego',
        'San Jose',
        'San Francisco',
        'Fresno',
        'Sacramento',
        'Long Beach',
        'Oakland',
        'Bakersfield',
        'Anaheim',
        'Santa Ana',
        'Riverside',
        'Stockton',
        'Irvine',
        'Chula Vista',
        'Fremont',
        'San Bernardino',
        'Modesto',
        'Fontana',
        'Oxnard',
        'Moreno Valley',
        'Huntington Beach',
        'Glendale',
        'Santa Clarita',
        'Garden Grove',
        'Oceanside',
        'Rancho Cucamonga',
        'Santa Rosa',
        'Ontario',
        'Lancaster',
        'Elk Grove',
        'Palmdale',
        'Corona',
        'Salinas',
        'Hayward',
        'Pomona',
        'Sunnyvale',
        'Escondido',
        'Torrance',
        'Pasadena',
        'Orange',
        'Fullerton',
        'Roseville',
        'Visalia',
        'Thousand Oaks',
        'Concord',
        'Simi Valley',
        'Santa Clara',
        'Victorville',
        'Vallejo',
    ),
    CO: cities(
        'Denver',
        'Colorado Springs',
        'Aurora',
        'Fort Collins',
        'Lakewood',
        'Thornton',
        'Arvada',
        'Westminster',
        'Pueblo',
        'Centennial',
        'Boulder',
        'Greeley',
        'Longmont',
        'Loveland',
        'Grand Junction',
        'Broomfield',
        'Castle Rock',
        'Commerce City',
        'Parker',
        'Littleton',
        'Northglenn',
        'Brighton',
        'Englewood',
        'Wheat Ridge',
        'Lafayette',
        'Fountain',
        'Windsor',
        'Erie',
        'Evans',
        'Golden',
        'Louisville',
        'Montrose',
        'Durango',
        'Cañon City',
        'Sterling',
        'Federal Heights',
        'Lone Tree',
        'Glendale',
        'Edgewater',
        'Aspen',
        'Steamboat Springs',
        'Fruita',
        'Cortez',
        'Trinidad',
        'Delta',
        'Alamosa',
        'Vail',
        'Manitou Springs',
        'Black Hawk',
        'Estes Park',
    ),
    CT: cities(
        'Bridgeport',
        'Stamford',
        'New Haven',
        'Hartford',
        'Waterbury',
        'Norwalk',
        'Danbury',
        'New Britain',
        'West Hartford',
        'Greenwich',
        'Fairfield',
        'Hamden',
        'Bristol',
        'Meriden',
        'Manchester',
        'West Haven',
        'Milford',
        'Stratford',
        'East Hartford',
        'Middletown',
        'Wallingford',
        'Southington',
        'Shelton',
        'Norwich',
        'Trumbull',
        'Glastonbury',
        'Naugatuck',
        'Newington',
        'Cheshire',
        'Vernon',
        'Windsor',
        'New London',
        'Branford',
        'New Milford',
        'Westport',
        'Wethersfield',
        'Farmington',
        'Ridgefield',
        'Bloomfield',
        'Watertown',
        'Guilford',
        'Torrington',
        'Avon',
        'Berlin',
        'Madison',
        'Simsbury',
        'Killingly',
        'Plainville',
        'Rocky Hill',
        'Monroe',
    ),
    DE: cities(
        'Wilmington',
        'Dover',
        'Newark',
        'Middletown',
        'Smyrna',
        'Milford',
        'Seaford',
        'Georgetown',
        'Elsmere',
        'New Castle',
        'Millsboro',
        'Laurel',
        'Harrington',
        'Camden',
        'Clayton',
        'Lewes',
        'Milton',
        'Selbyville',
        'Bridgeville',
        'Townsend',
        'Delmar',
        'Felton',
        'Greenwood',
        'Cheswold',
        'Frederica',
        'Rehoboth Beach',
        'Ocean View',
        'Dagsboro',
        'Bethany Beach',
        'Frankford',
    ),
    FL: cities(
        'Jacksonville',
        'Miami',
        'Tampa',
        'Orlando',
        'St. Petersburg',
        'Hialeah',
        'Port St. Lucie',
        'Cape Coral',
        'Tallahassee',
        'Fort Lauderdale',
        'Pembroke Pines',
        'Hollywood',
        'Gainesville',
        'Miramar',
        'Coral Springs',
        'Lehigh Acres',
        'Palm Bay',
        'West Palm Beach',
        'Clearwater',
        'Lakeland',
        'Pompano Beach',
        'Brandon',
        'Spring Hill',
        'Davie',
        'Riverview',
        'Miami Gardens',
        'Boca Raton',
        'Sunrise',
        'Plantation',
        'Deltona',
        'Palm Coast',
        'Largo',
        'Deerfield Beach',
        'Melbourne',
        'Boynton Beach',
        'Lauderhill',
        'Fort Myers',
        'Weston',
        'Kissimmee',
        'Homestead',
        'Delray Beach',
        'Tamarac',
        'Daytona Beach',
        'Wellington',
        'North Port',
        'Jupiter',
        'Port Orange',
        'Coconut Creek',
        'Sanford',
        'Margate',
    ),
    GA: cities(
        'Atlanta',
        'Augusta',
        'Columbus',
        'Macon',
        'Savannah',
        'Athens',
        'South Fulton',
        'Sandy Springs',
        'Roswell',
        'Johns Creek',
        'Warner Robins',
        'Albany',
        'Alpharetta',
        'Marietta',
        'Stonecrest',
        'Smyrna',
        'Brookhaven',
        'Valdosta',
        'Dunwoody',
        'Rome',
        'East Point',
        'Milton',
        'Gainesville',
        'Hinesville',
        'Peachtree Corners',
        'Newnan',
        'Dalton',
        'Douglasville',
        'Kennesaw',
        'LaGrange',
        'Statesboro',
        'Lawrenceville',
        'Duluth',
        'Stockbridge',
        'Carrollton',
        'Woodstock',
        'Canton',
        'Griffin',
        'McDonough',
        'Acworth',
        'Cartersville',
        'Pooler',
        'Tucker',
        'Union City',
        'Decatur',
        'Forest Park',
        'Suwanee',
        'Norcross',
        'Calhoun',
        'Snellville',
    ),
    HI: cities(
        'Honolulu',
        'Hilo',
        'Kailua',
        'Kaneohe',
        'Waipahu',
        'Pearl City',
        'Waimalu',
        'Mililani Town',
        'Kahului',
        'Ewa Gentry',
        'Kihei',
        'Makakilo',
        'Wahiawa',
        'Schofield Barracks',
        'Wailuku',
        'Halawa',
        'Kapolei',
        'Ewa Beach',
        'Royal Kunia',
        'Kailua-Kona',
        'Maili',
        'Ocean Pointe',
        'Waianae',
        'Nanakuli',
        'Lahaina',
        'Hawaiian Paradise Park',
        'Waipio',
        'Kalaoa',
        'Aiea',
        'Pukalani',
    ),
    ID: cities(
        'Boise',
        'Meridian',
        'Nampa',
        'Idaho Falls',
        'Caldwell',
        'Pocatello',
        "Coeur d'Alene",
        'Twin Falls',
        'Post Falls',
        'Rexburg',
        'Lewiston',
        'Eagle',
        'Kuna',
        'Moscow',
        'Mountain Home',
        'Ammon',
        'Chubbuck',
        'Hayden',
        'Garden City',
        'Jerome',
        'Burley',
        'Star',
        'Blackfoot',
        'Sandpoint',
        'Rathdrum',
        'Hailey',
        'Payette',
        'Emmett',
        'Rupert',
        'Heyburn',
        'Preston',
        'Weiser',
        'Mountain Home AFB',
        'Fruitland',
        'Buhl',
        'Salmon',
        'Kimberly',
        'Middleton',
        'McCall',
        'Gooding',
        'Filer',
        'Shelley',
        'Sun Valley',
        'Bonners Ferry',
        'St. Anthony',
        'Wendell',
        'Soda Springs',
        'Driggs',
        'Orofino',
        'Grangeville',
    ),
    IL: cities(
        'Chicago',
        'Aurora',
        'Naperville',
        'Joliet',
        'Rockford',
        'Springfield',
        'Elgin',
        'Peoria',
        'Champaign',
        'Waukegan',
        'Cicero',
        'Bloomington',
        'Arlington Heights',
        'Evanston',
        'Schaumburg',
        'Bolingbrook',
        'Decatur',
        'Palatine',
        'Skokie',
        'Des Plaines',
        'Orland Park',
        'Tinley Park',
        'Oak Lawn',
        'Berwyn',
        'Mount Prospect',
        'Normal',
        'Wheaton',
        'Hoffman Estates',
        'Oak Park',
        'Downers Grove',
        'Glenview',
        'Elmhurst',
        'Lombard',
        'Buffalo Grove',
        'Bartlett',
        'Crystal Lake',
        'Carol Stream',
        'Streamwood',
        'Quincy',
        'Urbana',
        'Plainfield',
        'Hanover Park',
        'Carpentersville',
        'Wheeling',
        'Park Ridge',
        'Addison',
        'Calumet City',
        'Northbrook',
        'Romeoville',
        'Pekin',
    ),
    IN: cities(
        'Indianapolis',
        'Fort Wayne',
        'Evansville',
        'South Bend',
        'Carmel',
        'Fishers',
        'Bloomington',
        'Hammond',
        'Gary',
        'Lafayette',
        'Noblesville',
        'Greenwood',
        'Muncie',
        'Anderson',
        'Terre Haute',
        'Westfield',
        'Elkhart',
        'Mishawaka',
        'Lawrence',
        'Jeffersonville',
        'Columbus',
        'Portage',
        'New Albany',
        'Goshen',
        'Kokomo',
        'Valparaiso',
        'Michigan City',
        'Westfield',
        'West Lafayette',
        'Marion',
        'Schererville',
        'Crown Point',
        'Plainfield',
        'Highland',
        'Munster',
        'Granger',
        'Zionsville',
        'East Chicago',
        'Hobart',
        'Brownsburg',
        'Logansport',
        'Vincennes',
        'Crawfordsville',
        'New Castle',
        'Richmond',
        'Connersville',
        'Huntington',
        'Frankfort',
        'Greenfield',
        'Shelbyville',
    ),
    IA: cities(
        'Des Moines',
        'Cedar Rapids',
        'Davenport',
        'Sioux City',
        'Iowa City',
        'Waterloo',
        'Ames',
        'West Des Moines',
        'Ankeny',
        'Council Bluffs',
        'Dubuque',
        'Urbandale',
        'Cedar Falls',
        'Marion',
        'Bettendorf',
        'Marshalltown',
        'Mason City',
        'Clinton',
        'Burlington',
        'Ottumwa',
        'Fort Dodge',
        'Muscatine',
        'Coralville',
        'Johnston',
        'North Liberty',
        'Altoona',
        'Newton',
        'Indianola',
        'Waukee',
        'Pleasant Hill',
        'Storm Lake',
        'Boone',
        'Spencer',
        'Oskaloosa',
        'Carroll',
        'Fairfield',
        'Le Mars',
        'Fort Madison',
        'Keokuk',
        'Decorah',
        'Webster City',
        'Charles City',
        'Knoxville',
        'Atlantic',
        'Estherville',
        'Mount Pleasant',
        'Grinnell',
        'Centerville',
        'Vinton',
        'Algona',
    ),
    KS: cities(
        'Wichita',
        'Overland Park',
        'Kansas City',
        'Olathe',
        'Topeka',
        'Lawrence',
        'Shawnee',
        'Lenexa',
        'Manhattan',
        'Salina',
        'Hutchinson',
        'Leavenworth',
        'Leawood',
        'Garden City',
        'Dodge City',
        'Derby',
        'Emporia',
        'Prairie Village',
        'Junction City',
        'Hays',
        'Pittsburg',
        'Liberal',
        'Newton',
        'Gardner',
        'Great Bend',
        'McPherson',
        'El Dorado',
        'Ottawa',
        'Winfield',
        'Arkansas City',
        'Andover',
        'Lansing',
        'Merriam',
        'Atchison',
        'Haysville',
        'Parsons',
        'Coffeyville',
        'Mission',
        'Bonner Springs',
        'Chanute',
        'Independence',
        'Augusta',
        'Beloit',
        'Concordia',
        'Wellington',
        'Goodland',
        'Pratt',
        'Iola',
        'Abilene',
        'Russell',
    ),
    KY: cities(
        'Louisville',
        'Lexington',
        'Bowling Green',
        'Owensboro',
        'Covington',
        'Hopkinsville',
        'Richmond',
        'Florence',
        'Georgetown',
        'Henderson',
        'Elizabethtown',
        'Jeffersontown',
        'Independence',
        'Frankfort',
        'Paducah',
        'Nicholasville',
        'Radcliff',
        'Ashland',
        'Madisonville',
        'Murray',
        'Erlanger',
        'Winchester',
        'St. Matthews',
        'Burlington',
        'Danville',
        'Fort Thomas',
        'Newport',
        'Shively',
        'Shelbyville',
        'Glasgow',
        'Berea',
        'Mount Washington',
        'Bardstown',
        'Shepherdsville',
        'Somerset',
        'Lyndon',
        'Lawrenceburg',
        'Middlesborough',
        'Mayfield',
        'Campbellsville',
        'Maysville',
        'Versailles',
        'Paris',
        'Hazard',
        'Russellville',
        'Pikeville',
        'Corbin',
        'Harrodsburg',
        'Morehead',
        'Princeton',
    ),
    LA: cities(
        'New Orleans',
        'Baton Rouge',
        'Shreveport',
        'Lafayette',
        'Lake Charles',
        'Kenner',
        'Bossier City',
        'Monroe',
        'Alexandria',
        'Houma',
        'Marrero',
        'New Iberia',
        'Slidell',
        'Central',
        'Prairieville',
        'Terrytown',
        'Ruston',
        'Sulphur',
        'Hammond',
        'Harvey',
        'Natchitoches',
        'Gretna',
        'Opelousas',
        'Chalmette',
        'Estelle',
        'Zachary',
        'Pineville',
        'Bayou Cane',
        'Crowley',
        'Thibodaux',
        'Minden',
        'Morgan City',
        'Mandeville',
        'DeRidder',
        'Eunice',
        'Abbeville',
        'Bogalusa',
        'Baker',
        'Covington',
        'Denham Springs',
        'Bastrop',
        'West Monroe',
        'Jennings',
        'Plaquemine',
        'Ponchatoula',
        'Walker',
        'Leesville',
        'Westwego',
        'Carencro',
        'Donaldsonville',
    ),
    ME: cities(
        'Portland',
        'Lewiston',
        'Bangor',
        'South Portland',
        'Auburn',
        'Biddeford',
        'Sanford',
        'Brunswick',
        'Saco',
        'Westbrook',
        'Augusta',
        'Waterville',
        'Scarborough',
        'Windham',
        'Gorham',
        'York',
        'Falmouth',
        'Cape Elizabeth',
        'Old Orchard Beach',
        'Kennebunk',
        'Topsham',
        'Bath',
        'Yarmouth',
        'Standish',
        'Cumberland',
        'Buxton',
        'Lisbon',
        'Wells',
        'Ellsworth',
        'Hampden',
        'Caribou',
        'Presque Isle',
        'Old Town',
        'Rockland',
        'Belfast',
        'Skowhegan',
        'Calais',
        'Eastport',
        'Houlton',
        'Madawaska',
        'Camden',
        'Bar Harbor',
        'Rumford',
        'Norway',
        'Farmington',
        'Boothbay Harbor',
        'Damariscotta',
        'Wiscasset',
        'Brewer',
        'Orono',
    ),
    MD: cities(
        'Baltimore',
        'Frederick',
        'Rockville',
        'Gaithersburg',
        'Bowie',
        'Hagerstown',
        'Annapolis',
        'College Park',
        'Salisbury',
        'Laurel',
        'Greenbelt',
        'Cumberland',
        'Westminster',
        'Hyattsville',
        'Takoma Park',
        'Easton',
        'Elkton',
        'Aberdeen',
        'Havre de Grace',
        'Cambridge',
        'New Carrollton',
        'Bel Air',
        'Riverdale Park',
        'Frostburg',
        'Mount Airy',
        'Glenarden',
        'Ocean City',
        'Cheverly',
        'Forest Heights',
        'District Heights',
        'Edmonston',
        'University Park',
        'Brentwood',
        'Bladensburg',
        'North East',
        'Chestertown',
        'Pocomoke City',
        'Crisfield',
        'Berlin',
        'Snow Hill',
        'Princess Anne',
        'Denton',
        'Centreville',
        'La Plata',
        'Leonardtown',
        'Lonaconing',
        'Oakland',
        'Westernport',
        'Brunswick',
        'Thurmont',
    ),
    MA: cities(
        'Boston',
        'Worcester',
        'Springfield',
        'Cambridge',
        'Lowell',
        'Brockton',
        'Quincy',
        'Lynn',
        'New Bedford',
        'Fall River',
        'Newton',
        'Lawrence',
        'Somerville',
        'Framingham',
        'Haverhill',
        'Waltham',
        'Malden',
        'Brookline',
        'Plymouth',
        'Medford',
        'Taunton',
        'Chicopee',
        'Weymouth',
        'Revere',
        'Peabody',
        'Methuen',
        'Barnstable',
        'Pittsfield',
        'Attleboro',
        'Arlington',
        'Everett',
        'Salem',
        'Westfield',
        'Leominster',
        'Fitchburg',
        'Beverly',
        'Holyoke',
        'Marlborough',
        'Woburn',
        'Chelsea',
        'Braintree',
        'Watertown',
        'Lexington',
        'Franklin',
        'Andover',
        'North Andover',
        'Natick',
        'Needham',
        'Randolph',
        'West Springfield',
    ),
    MI: cities(
        'Detroit',
        'Grand Rapids',
        'Warren',
        'Sterling Heights',
        'Ann Arbor',
        'Lansing',
        'Dearborn',
        'Livonia',
        'Troy',
        'Westland',
        'Farmington Hills',
        'Kalamazoo',
        'Wyoming',
        'Southfield',
        'Rochester Hills',
        'Taylor',
        'Pontiac',
        'Saint Clair Shores',
        'Royal Oak',
        'Novi',
        'Dearborn Heights',
        'Battle Creek',
        'Saginaw',
        'Kentwood',
        'East Lansing',
        'Roseville',
        'Portage',
        'Midland',
        'Lincoln Park',
        'Muskegon',
        'Bay City',
        'Holland',
        'Jackson',
        'Eastpointe',
        'Burton',
        'Madison Heights',
        'Oak Park',
        'Southgate',
        'Marquette',
        'Port Huron',
        'Garden City',
        'Inkster',
        'Mount Pleasant',
        'Allen Park',
        'Trenton',
        'Wyandotte',
        'Birmingham',
        'Forest Hills',
        'Auburn Hills',
        'Hamtramck',
    ),
    MN: cities(
        'Minneapolis',
        'Saint Paul',
        'Rochester',
        'Bloomington',
        'Duluth',
        'Brooklyn Park',
        'Plymouth',
        'Maple Grove',
        'Woodbury',
        'St. Cloud',
        'Eagan',
        'Eden Prairie',
        'Coon Rapids',
        'Burnsville',
        'Blaine',
        'Lakeville',
        'Minnetonka',
        'Apple Valley',
        'Edina',
        'St. Louis Park',
        'Mankato',
        'Maplewood',
        'Moorhead',
        'Shakopee',
        'Cottage Grove',
        'Inver Grove Heights',
        'Roseville',
        'Andover',
        'Brooklyn Center',
        'Savage',
        'Fridley',
        'Oakdale',
        'Chaska',
        'Ramsey',
        'Prior Lake',
        'Winona',
        'Rosemount',
        'Faribault',
        'Owatonna',
        'Hopkins',
        'Northfield',
        'Crystal',
        'New Hope',
        'Chanhassen',
        'Hastings',
        'Stillwater',
        'New Brighton',
        'Forest Lake',
        'Anoka',
        'Champlin',
    ),
    MS: cities(
        'Jackson',
        'Gulfport',
        'Southaven',
        'Hattiesburg',
        'Biloxi',
        'Olive Branch',
        'Tupelo',
        'Meridian',
        'Greenville',
        'Madison',
        'Horn Lake',
        'Pearl',
        'Starkville',
        'Ridgeland',
        'Columbus',
        'Vicksburg',
        'Pascagoula',
        'Clinton',
        'Brandon',
        'Oxford',
        'Gautier',
        'Laurel',
        'Hernando',
        'Long Beach',
        'Natchez',
        'Greenwood',
        'Ocean Springs',
        'Corinth',
        'Brookhaven',
        'Cleveland',
        'McComb',
        'Grenada',
        'Byram',
        'Clarksdale',
        'Bay St. Louis',
        'Picayune',
        'Yazoo City',
        'Petal',
        'Canton',
        'Moss Point',
        'Pass Christian',
        'Senatobia',
        'Indianola',
        'Booneville',
        'West Point',
        'Diamondhead',
        'Batesville',
        'Holly Springs',
        'New Albany',
        'Forest',
    ),
    MO: cities(
        'Kansas City',
        'St. Louis',
        'Springfield',
        'Columbia',
        'Independence',
        "Lee's Summit",
        "O'Fallon",
        'St. Joseph',
        'St. Charles',
        'St. Peters',
        'Blue Springs',
        'Florissant',
        'Joplin',
        'Chesterfield',
        'Jefferson City',
        'Cape Girardeau',
        'Wildwood',
        'University City',
        'Wentzville',
        'Ballwin',
        'Raytown',
        'Liberty',
        'Kirkwood',
        'Maryland Heights',
        'Hazelwood',
        'Gladstone',
        'Grandview',
        'Belton',
        'Webster Groves',
        'Sedalia',
        'Rolla',
        'Raymore',
        'Ferguson',
        'Nixa',
        'Warrensburg',
        'Mehlville',
        'Arnold',
        'Ozark',
        'Manchester',
        'Republic',
        'Poplar Bluff',
        'Branson',
        'Lebanon',
        'Hannibal',
        'Kirksville',
        'Cottleville',
        'Sikeston',
        'Carthage',
        'Marshall',
        'Excelsior Springs',
    ),
    MT: cities(
        'Billings',
        'Missoula',
        'Great Falls',
        'Bozeman',
        'Butte',
        'Helena',
        'Kalispell',
        'Belgrade',
        'Havre',
        'Anaconda',
        'Miles City',
        'Whitefish',
        'Livingston',
        'Laurel',
        'Sidney',
        'Polson',
        'Lewistown',
        'Hamilton',
        'Glendive',
        'Dillon',
        'Hardin',
        'Glasgow',
        'Columbia Falls',
        'Shelby',
        'Wolf Point',
        'Cut Bank',
        'Libby',
        'Conrad',
        'Deer Lodge',
        'Red Lodge',
        'Roundup',
        'Malta',
        'Plentywood',
        'Manhattan',
        'Three Forks',
        'Stevensville',
        'Big Timber',
        'Choteau',
        'Townsend',
        'Eureka',
        'Forsyth',
        'Baker',
        'Fairview',
        'Plains',
        'Boulder',
        'Chinook',
        'Browning',
        'Fort Benton',
        'Philipsburg',
        'Lincoln',
    ),
    NE: cities(
        'Omaha',
        'Lincoln',
        'Bellevue',
        'Grand Island',
        'Kearney',
        'Fremont',
        'Hastings',
        'Norfolk',
        'North Platte',
        'Papillion',
        'Columbus',
        'La Vista',
        'Scottsbluff',
        'South Sioux City',
        'Beatrice',
        'Lexington',
        'Gering',
        'Alliance',
        'Blair',
        'McCook',
        'York',
        'Seward',
        'Crete',
        'Sidney',
        'Plattsmouth',
        'Schuyler',
        'Ralston',
        'Wayne',
        'Holdrege',
        'Chadron',
        'Aurora',
        "O'Neill",
        'Cozad',
        'Fairbury',
        'Gretna',
        'Nebraska City',
        'West Point',
        'Ogallala',
        'Wahoo',
        'Falls City',
        'Auburn',
        'Broken Bow',
        'Hickman',
        'Central City',
        'Gothenburg',
        'Valentine',
        'Madison',
        'Tekamah',
        'Imperial',
        'Albion',
    ),
    NV: cities(
        'Las Vegas',
        'Henderson',
        'Reno',
        'North Las Vegas',
        'Enterprise',
        'Spring Valley',
        'Sunrise Manor',
        'Paradise',
        'Sparks',
        'Carson City',
        'Pahrump',
        'Whitney',
        'Winchester',
        'Summerlin South',
        'Elko',
        'Mesquite',
        'Boulder City',
        'Fernley',
        'Spanish Springs',
        'Spring Creek',
        'Cold Springs',
        'Sun Valley',
        'Gardnerville',
        'Dayton',
        'Fallon',
        'Winnemucca',
        'Lemmon Valley',
        'Indian Hills',
        'Incline Village',
        'Yerington',
        'Sandy Valley',
        'Moapa Valley',
        'West Wendover',
        'Carlin',
        'Lovelock',
        'Tonopah',
        'Wells',
        'Caliente',
        'Eureka',
        'Goldfield',
        'Pioche',
        'Battle Mountain',
        'Round Mountain',
        'Beatty',
        'Hawthorne',
        'Silver Springs',
        'Smith Valley',
        'Verdi',
        'Stagecoach',
        'McGill',
    ),
    NH: cities(
        'Manchester',
        'Nashua',
        'Concord',
        'Derry',
        'Dover',
        'Rochester',
        'Salem',
        'Merrimack',
        'Hudson',
        'Londonderry',
        'Keene',
        'Bedford',
        'Portsmouth',
        'Goffstown',
        'Laconia',
        'Hampton',
        'Milford',
        'Durham',
        'Exeter',
        'Windham',
        'Hooksett',
        'Claremont',
        'Lebanon',
        'Pelham',
        'Somersworth',
        'Plaistow',
        'Berlin',
        'Franklin',
        'Hanover',
        'Newmarket',
        'Conway',
        'Wolfeboro',
        'Peterborough',
        'Littleton',
        'Plymouth',
        'Henniker',
        'Jaffrey',
        'Tilton',
        'Newport',
        'Lincoln',
        'Whitefield',
        'Lancaster',
        'North Conway',
        'Meredith',
        'Gorham',
        'Pittsfield',
        'Hillsborough',
        'Bristol',
        'Antrim',
        'Charlestown',
    ),
    NJ: cities(
        'Newark',
        'Jersey City',
        'Paterson',
        'Elizabeth',
        'Lakewood',
        'Edison',
        'Woodbridge',
        'Toms River',
        'Hamilton',
        'Trenton',
        'Clifton',
        'Camden',
        'Brick',
        'Cherry Hill',
        'Passaic',
        'Middletown',
        'Union City',
        'Old Bridge',
        'Gloucester Township',
        'Bayonne',
        'East Orange',
        'Vineland',
        'New Brunswick',
        'Hoboken',
        'Perth Amboy',
        'West New York',
        'Plainfield',
        'Bloomfield',
        'East Brunswick',
        'West Orange',
        'Sayreville',
        'Hackensack',
        'Kearny',
        'Linden',
        'Atlantic City',
        'Fort Lee',
        'Garfield',
        'Long Branch',
        'Westfield',
        'Princeton',
        'Englewood',
        'Fair Lawn',
        'Wayne',
        'Belleville',
        'Cliffside Park',
        'North Bergen',
        'Irvington',
        'Montclair',
        'West Milford',
        'Parsippany',
    ),
    NM: cities(
        'Albuquerque',
        'Las Cruces',
        'Rio Rancho',
        'Santa Fe',
        'Roswell',
        'Farmington',
        'Clovis',
        'Hobbs',
        'Alamogordo',
        'Carlsbad',
        'Gallup',
        'Los Lunas',
        'Sunland Park',
        'Las Vegas',
        'Deming',
        'Chaparral',
        'Portales',
        'Los Alamos',
        'North Valley',
        'Lovington',
        'Artesia',
        'South Valley',
        'Anthony',
        'Silver City',
        'Espanola',
        'Bernalillo',
        'Grants',
        'Aztec',
        'Bloomfield',
        'Belen',
        'Socorro',
        'Corrales',
        'Edgewood',
        'Truth or Consequences',
        'Ruidoso',
        'Tucumcari',
        'Raton',
        'Taos',
        'Eldorado at Santa Fe',
        'Lordsburg',
        'Shiprock',
        'Cuba',
        'Tularosa',
        'Bayard',
        'Mesilla',
        'Estancia',
        'Capitan',
        'Mountainair',
        'Fort Sumner',
        'Magdalena',
    ),
    NY: cities(
        'New York City',
        'Buffalo',
        'Yonkers',
        'Rochester',
        'Syracuse',
        'Albany',
        'New Rochelle',
        'Mount Vernon',
        'Schenectady',
        'Utica',
        'White Plains',
        'Hempstead',
        'Troy',
        'Niagara Falls',
        'Binghamton',
        'Freeport',
        'Valley Stream',
        'Long Beach',
        'Rome',
        'Ithaca',
        'North Tonawanda',
        'Poughkeepsie',
        'Jamestown',
        'Saratoga Springs',
        'Glens Falls',
        'Newburgh',
        'Spring Valley',
        'Middletown',
        'Elmira',
        'Auburn',
        'Watertown',
        'Lockport',
        'Plattsburgh',
        'Garden City',
        'Mineola',
        'Kingston',
        'Oneonta',
        'Cortland',
        'Geneva',
        'Tonawanda',
        'Oswego',
        'Glen Cove',
        'Beacon',
        'Mamaroneck',
        'Lackawanna',
        'Peekskill',
        'Rye',
        'Cohoes',
        'Batavia',
        'Olean',
    ),
    NC: cities(
        'Charlotte',
        'Raleigh',
        'Greensboro',
        'Durham',
        'Winston-Salem',
        'Fayetteville',
        'Cary',
        'Wilmington',
        'High Point',
        'Concord',
        'Asheville',
        'Greenville',
        'Gastonia',
        'Jacksonville',
        'Apex',
        'Chapel Hill',
        'Wake Forest',
        'Burlington',
        'Huntersville',
        'Rocky Mount',
        'Hickory',
        'Kannapolis',
        'Indian Trail',
        'Mooresville',
        'Wilson',
        'Sanford',
        'Salisbury',
        'Holly Springs',
        'Garner',
        'Fuquay-Varina',
        'New Bern',
        'Goldsboro',
        'Mint Hill',
        'Matthews',
        'Statesville',
        'Cornelius',
        'Morrisville',
        'Asheboro',
        'Monroe',
        'Thomasville',
        'Lumberton',
        'Kernersville',
        'Mocksville',
        'Carrboro',
        'Knightdale',
        'Pinehurst',
        'Boone',
        'Lexington',
        'Shelby',
        'Eden',
    ),
    ND: cities(
        'Fargo',
        'Bismarck',
        'Grand Forks',
        'Minot',
        'West Fargo',
        'Williston',
        'Dickinson',
        'Mandan',
        'Jamestown',
        'Wahpeton',
        'Devils Lake',
        'Watford City',
        'Valley City',
        'Grafton',
        'Lincoln',
        'Beulah',
        'Rugby',
        'Stanley',
        'Horace',
        'Casselton',
        'New Town',
        'Hazen',
        'Bottineau',
        'Tioga',
        'Lisbon',
        'Carrington',
        'Mayville',
        'Garrison',
        'Larimore',
        'Cavalier',
        'Oakes',
        'Park River',
        'Hillsboro',
        'Harvey',
        'Killdeer',
        'Crosby',
        'Cooperstown',
        'Surrey',
        'Linton',
        'Rolla',
        'Bowman',
        'Velva',
        'Ellendale',
        'Hettinger',
        'Underwood',
        'New Rockford',
        'Northwood',
        'Mott',
        'Edgeley',
        'Walhalla',
    ),
    OH: cities(
        'Columbus',
        'Cleveland',
        'Cincinnati',
        'Toledo',
        'Akron',
        'Dayton',
        'Parma',
        'Canton',
        'Youngstown',
        'Lorain',
        'Hamilton',
        'Springfield',
        'Kettering',
        'Elyria',
        'Lakewood',
        'Cuyahoga Falls',
        'Middletown',
        'Newark',
        'Mansfield',
        'Mentor',
        'Beavercreek',
        'Cleveland Heights',
        'Strongsville',
        'Dublin',
        'Fairfield',
        'Findlay',
        'Warren',
        'Lancaster',
        'Lima',
        'Huber Heights',
        'Westerville',
        'Marion',
        'Grove City',
        'Reynoldsburg',
        'Delaware',
        'Brunswick',
        'Upper Arlington',
        'Stow',
        'Gahanna',
        'Westlake',
        'North Olmsted',
        'North Royalton',
        'Massillon',
        'Mason',
        'Bowling Green',
        'Fairborn',
        'Garfield Heights',
        'Shaker Heights',
        'Sandusky',
        'Maple Heights',
    ),
    OK: cities(
        'Oklahoma City',
        'Tulsa',
        'Norman',
        'Broken Arrow',
        'Edmond',
        'Lawton',
        'Moore',
        'Midwest City',
        'Enid',
        'Stillwater',
        'Owasso',
        'Muskogee',
        'Bartlesville',
        'Shawnee',
        'Yukon',
        'Bixby',
        'Ardmore',
        'Ponca City',
        'Duncan',
        'Del City',
        'Jenks',
        'Sapulpa',
        'Mustang',
        'Bethany',
        'Sand Springs',
        'Altus',
        'Claremore',
        'McAlester',
        'El Reno',
        'Choctaw',
        'Durant',
        'Ada',
        'Tahlequah',
        'Chickasha',
        'Miami',
        'Glenpool',
        'Coweta',
        'Elk City',
        'Woodward',
        'Weatherford',
        'Guymon',
        'Guthrie',
        'Warr Acres',
        'Pryor Creek',
        'The Village',
        'Wagoner',
        'Catoosa',
        'Skiatook',
        'Newcastle',
        'Vinita',
    ),
    OR: cities(
        'Portland',
        'Eugene',
        'Salem',
        'Gresham',
        'Hillsboro',
        'Bend',
        'Beaverton',
        'Medford',
        'Springfield',
        'Corvallis',
        'Albany',
        'Tigard',
        'Lake Oswego',
        'Keizer',
        'Grants Pass',
        'Oregon City',
        'McMinnville',
        'Redmond',
        'Tualatin',
        'West Linn',
        'Woodburn',
        'Forest Grove',
        'Newberg',
        'Wilsonville',
        'Roseburg',
        'Klamath Falls',
        'Ashland',
        'Milwaukie',
        'Sherwood',
        'Happy Valley',
        'Central Point',
        'Hermiston',
        'Pendleton',
        'Troutdale',
        'Canby',
        'Lebanon',
        'Coos Bay',
        'Dallas',
        'St. Helens',
        'The Dalles',
        'La Grande',
        'Cornelius',
        'Gladstone',
        'Ontario',
        'Sandy',
        'Astoria',
        'Newport',
        'Monmouth',
        'Prineville',
        'Baker City',
    ),
    PA: cities(
        'Philadelphia',
        'Pittsburgh',
        'Allentown',
        'Erie',
        'Reading',
        'Scranton',
        'Bethlehem',
        'Lancaster',
        'Harrisburg',
        'York',
        'Wilkes-Barre',
        'State College',
        'Altoona',
        'Chester',
        'Williamsport',
        'Norristown',
        'Easton',
        'Lebanon',
        'Hazleton',
        'New Castle',
        'Pottstown',
        'McKeesport',
        'Johnstown',
        'West Chester',
        'Pottsville',
        'Greensburg',
        'Hanover',
        'Sharon',
        'Carlisle',
        'Hermitage',
        'Bristol',
        'Phoenixville',
        'Butler',
        'Chambersburg',
        'Lansdale',
        'Indiana',
        'Sunbury',
        'New Kensington',
        'Meadville',
        'Connellsville',
        'Beaver Falls',
        'Du Bois',
        'Lock Haven',
        'Oil City',
        'Uniontown',
        'Bloomsburg',
        'Carbondale',
        'St. Marys',
        'Lewistown',
        'Coatesville',
    ),
    RI: cities(
        'Providence',
        'Cranston',
        'Warwick',
        'Pawtucket',
        'East Providence',
        'Woonsocket',
        'Coventry',
        'North Providence',
        'Cumberland',
        'West Warwick',
        'Johnston',
        'Bristol',
        'Newport',
        'Westerly',
        'South Kingstown',
        'Lincoln',
        'Smithfield',
        'Central Falls',
        'Portsmouth',
        'Barrington',
        'Tiverton',
        'East Greenwich',
        'Burrillville',
        'Middletown',
        'Narragansett',
        'Warren',
        'Scituate',
        'Glocester',
        'Charlestown',
        'Hopkinton',
        'Richmond',
        'Foster',
        'Jamestown',
        'Little Compton',
        'New Shoreham',
    ),
    SC: cities(
        'Charleston',
        'Columbia',
        'North Charleston',
        'Mount Pleasant',
        'Rock Hill',
        'Greenville',
        'Summerville',
        'Sumter',
        'Goose Creek',
        'Hilton Head Island',
        'Florence',
        'Spartanburg',
        'Aiken',
        'Myrtle Beach',
        'Anderson',
        'Greer',
        'Mauldin',
        'Greenwood',
        'North Augusta',
        'Simpsonville',
        'Easley',
        'Hanahan',
        'Lexington',
        'Conway',
        'West Columbia',
        'North Myrtle Beach',
        'Clemson',
        'Orangeburg',
        'Cayce',
        'Bluffton',
        'Beaufort',
        'Gaffney',
        'Forest Acres',
        'Newberry',
        'Camden',
        'Tega Cay',
        'Port Royal',
        'Hartsville',
        'Fort Mill',
        'York',
        'Travelers Rest',
        'Laurens',
        'Georgetown',
        'Union',
        'Walterboro',
        'Seneca',
        'Lancaster',
        'Bennettsville',
        'Marion',
        'Dillon',
    ),
    SD: cities(
        'Sioux Falls',
        'Rapid City',
        'Aberdeen',
        'Brookings',
        'Watertown',
        'Mitchell',
        'Yankton',
        'Pierre',
        'Huron',
        'Vermillion',
        'Spearfish',
        'Brandon',
        'Box Elder',
        'Madison',
        'Sturgis',
        'Belle Fourche',
        'Harrisburg',
        'Tea',
        'Mobridge',
        'Lead',
        'Hot Springs',
        'Milbank',
        'Dell Rapids',
        'Winner',
        'Canton',
        'Sisseton',
        'North Sioux City',
        'Hartford',
        'Redfield',
        'Custer',
        'Beresford',
        'Chamberlain',
        'Lemmon',
        'Deadwood',
        'Webster',
        'Flandreau',
        'Gettysburg',
        'Wagner',
        'Mission',
        'Eagle Butte',
        'Platte',
        'Parkston',
        'Tyndall',
        'Garretson',
        'Salem',
        'Wessington Springs',
        'Clear Lake',
        'Britton',
        'Faulkton',
        'Onida',
    ),
    TN: cities(
        'Nashville',
        'Memphis',
        'Knoxville',
        'Chattanooga',
        'Clarksville',
        'Murfreesboro',
        'Franklin',
        'Jackson',
        'Johnson City',
        'Bartlett',
        'Hendersonville',
        'Kingsport',
        'Smyrna',
        'Collierville',
        'Cleveland',
        'Brentwood',
        'Germantown',
        'Columbia',
        'La Vergne',
        'Spring Hill',
        'Mount Juliet',
        'Gallatin',
        'Cookeville',
        'Lebanon',
        'Morristown',
        'Oak Ridge',
        'Maryville',
        'Bristol',
        'Farragut',
        'Shelbyville',
        'Tullahoma',
        'East Ridge',
        'Dyersburg',
        'Sevierville',
        'Goodlettsville',
        'Athens',
        'Greeneville',
        'McMinnville',
        'Springfield',
        'Crossville',
        'Lawrenceburg',
        'Lakeland',
        'Soddy-Daisy',
        'Dickson',
        'Paris',
        'Union City',
        'Millington',
        'Lexington',
        'Manchester',
        'Pulaski',
    ),
    TX: cities(
        'Houston',
        'San Antonio',
        'Dallas',
        'Austin',
        'Fort Worth',
        'El Paso',
        'Arlington',
        'Corpus Christi',
        'Plano',
        'Laredo',
        'Lubbock',
        'Irving',
        'Garland',
        'Frisco',
        'McKinney',
        'Amarillo',
        'Grand Prairie',
        'Brownsville',
        'Killeen',
        'Pasadena',
        'McAllen',
        'Mesquite',
        'Midland',
        'Denton',
        'Waco',
        'Carrollton',
        'Round Rock',
        'Abilene',
        'Pearland',
        'Richardson',
        'Odessa',
        'Sugar Land',
        'Beaumont',
        'Lewisville',
        'Tyler',
        'College Station',
        'League City',
        'Allen',
        'Wichita Falls',
        'San Angelo',
        'Edinburg',
        'Conroe',
        'Bryan',
        'Mission',
        'New Braunfels',
        'Pharr',
        'Longview',
        'Baytown',
        'Cedar Park',
        'Temple',
    ),
    UT: cities(
        'Salt Lake City',
        'West Valley City',
        'West Jordan',
        'Provo',
        'St. George',
        'Orem',
        'Sandy',
        'Ogden',
        'Lehi',
        'South Jordan',
        'Layton',
        'Millcreek',
        'Taylorsville',
        'Logan',
        'Murray',
        'Draper',
        'Bountiful',
        'Riverton',
        'Herriman',
        'Spanish Fork',
        'Roy',
        'Pleasant Grove',
        'Tooele',
        'Cedar City',
        'Kearns',
        'Springville',
        'Cottonwood Heights',
        'Midvale',
        'Holladay',
        'American Fork',
        'Eagle Mountain',
        'Saratoga Springs',
        'Syracuse',
        'Clearfield',
        'Washington',
        'South Salt Lake',
        'Farmington',
        'Magna',
        'Clinton',
        'Kaysville',
        'North Ogden',
        'Payson',
        'North Salt Lake',
        'Centerville',
        'Highland',
        'Hurricane',
        'Hyrum',
        'Heber City',
        'Brigham City',
        'Vernal',
    ),
    VT: cities(
        'Burlington',
        'South Burlington',
        'Colchester',
        'Rutland',
        'Essex Junction',
        'Bennington',
        'Brattleboro',
        'Hartford',
        'Milton',
        'Williston',
        'Barre',
        'Springfield',
        'Montpelier',
        'St. Albans',
        'Middlebury',
        'Newport',
        'Vergennes',
        'Winooski',
        'St. Johnsbury',
        'Lyndon',
        'Morristown',
        'Northfield',
        'Manchester',
        'Shelburne',
        'Hinesburg',
        'Jericho',
        'Stowe',
        'Rockingham',
        'Swanton',
        'Waterbury',
        'Lyndonville',
        'Woodstock',
        'Richmond',
        'Wilmington',
        'Bristol',
        'Castleton',
        'Hardwick',
        'Randolph',
        'Killington',
        'Ludlow',
        'White River Junction',
        'Quechee',
        'Charlotte',
        'Underhill',
        'Norwich',
        'Brandon',
        'Putney',
        'Chester',
        'Poultney',
        'Fair Haven',
    ),
    VA: cities(
        'Virginia Beach',
        'Chesapeake',
        'Norfolk',
        'Arlington',
        'Richmond',
        'Newport News',
        'Alexandria',
        'Hampton',
        'Roanoke',
        'Portsmouth',
        'Suffolk',
        'Lynchburg',
        'Centreville',
        'Reston',
        'Dale City',
        'Charlottesville',
        'Tuckahoe',
        'McLean',
        'Mechanicsville',
        'Tysons',
        'Leesburg',
        'Annandale',
        'Burke',
        'Springfield',
        'Glen Allen',
        'Blacksburg',
        'Manassas',
        'Harrisonburg',
        'Danville',
        'Salem',
        'Petersburg',
        'Cave Spring',
        'Herndon',
        'Linton Hall',
        'Lake Ridge',
        'Staunton',
        'South Riding',
        'Fredericksburg',
        'Winchester',
        'Front Royal',
        'Christiansburg',
        'Williamsburg',
        'Lorton',
        'Hopewell',
        'Vienna',
        'Manassas Park',
        'Falls Church',
        'Bristol',
        'Waynesboro',
        'Radford',
    ),
    WA: cities(
        'Seattle',
        'Spokane',
        'Tacoma',
        'Vancouver',
        'Bellevue',
        'Kent',
        'Everett',
        'Renton',
        'Federal Way',
        'Yakima',
        'Spokane Valley',
        'Kirkland',
        'Bellingham',
        'Auburn',
        'Pasco',
        'Marysville',
        'Lakewood',
        'Redmond',
        'Sammamish',
        'Shoreline',
        'Richland',
        'Olympia',
        'Burien',
        'Kennewick',
        'Bothell',
        'Edmonds',
        'Puyallup',
        'Lynnwood',
        'Bremerton',
        'Issaquah',
        'Lacey',
        'Wenatchee',
        'Mount Vernon',
        'University Place',
        'Walla Walla',
        'Maple Valley',
        'Pullman',
        'Des Moines',
        'SeaTac',
        'Mercer Island',
        'Tukwila',
        'Bainbridge Island',
        'Mukilteo',
        'Tumwater',
        'Camas',
        'Battle Ground',
        'Mountlake Terrace',
        'Oak Harbor',
        'Moses Lake',
        'Kenmore',
    ),
    WV: cities(
        'Charleston',
        'Huntington',
        'Morgantown',
        'Parkersburg',
        'Wheeling',
        'Weirton',
        'Fairmont',
        'Martinsburg',
        'Beckley',
        'Clarksburg',
        'Bluefield',
        'South Charleston',
        'St. Albans',
        'Vienna',
        'Bridgeport',
        'Cross Lanes',
        'Teays Valley',
        'Hurricane',
        'Elkins',
        'Nitro',
        'Princeton',
        'Lewisburg',
        'Buckhannon',
        'Keyser',
        'New Martinsville',
        'Grafton',
        'Ranson',
        'Moundsville',
        'Oak Hill',
        'Dunbar',
        'Westover',
        'Williamson',
        'Logan',
        'Welch',
        'Sistersville',
        'Mullens',
        'Summersville',
        'Ripley',
        'Petersburg',
        'Romney',
        'Spencer',
        'Charles Town',
        'Madison',
        'Kingwood',
        'Philippi',
        'Point Pleasant',
        'Mannington',
        'Ravenswood',
        'Chester',
        'War',
    ),
    WI: cities(
        'Milwaukee',
        'Madison',
        'Green Bay',
        'Kenosha',
        'Racine',
        'Appleton',
        'Waukesha',
        'Eau Claire',
        'Oshkosh',
        'Janesville',
        'West Allis',
        'La Crosse',
        'Sheboygan',
        'Wauwatosa',
        'Fond du Lac',
        'New Berlin',
        'Wausau',
        'Brookfield',
        'Greenfield',
        'Beloit',
        'Franklin',
        'Sun Prairie',
        'Manitowoc',
        'Oak Creek',
        'Menomonee Falls',
        'West Bend',
        'Mount Pleasant',
        'Superior',
        'De Pere',
        'Stevens Point',
        'Neenah',
        'Caledonia',
        'Fitchburg',
        'Muskego',
        'Watertown',
        'Pleasant Prairie',
        'South Milwaukee',
        'Marshfield',
        'Mequon',
        'Onalaska',
        'Cudahy',
        'Wisconsin Rapids',
        'Beaver Dam',
        'Howard',
        'Middleton',
        'Menomonie',
        'Ashwaubenon',
        'Glendale',
        'Whitewater',
        'Burlington',
    ),
    WY: cities(
        'Cheyenne',
        'Casper',
        'Laramie',
        'Gillette',
        'Rock Springs',
        'Sheridan',
        'Green River',
        'Evanston',
        'Riverton',
        'Jackson',
        'Cody',
        'Rawlins',
        'Lander',
        'Torrington',
        'Powell',
        'Douglas',
        'Worland',
        'Buffalo',
        'Wheatland',
        'Newcastle',
        'Mills',
        'Thermopolis',
        'Kemmerer',
        'Glenrock',
        'Lovell',
        'Lyman',
        'Afton',
        'Pinedale',
        'Wright',
        'Mountain View',
        'Saratoga',
        'Star Valley Ranch',
        'Greybull',
        'Sundance',
        'Lusk',
        'Diamondville',
        'Hanna',
        'Marbleton',
        'Big Piney',
        'Encampment',
        'Pine Bluffs',
        'Basin',
        'Dubois',
        'Hudson',
        'Cokeville',
        'La Barge',
        'Kaycee',
        'Medicine Bow',
        'Hulett',
        'Moorcroft',
    ),
};
